import {
  getRequiredFields,
  removeEmptyFields,
  setEmptyFieldsToNull,
} from "@/lib/utils";
import { AS_ON, KPL, MBAPL, SGFL, SIPL } from "./consts";
import { zodResolver } from "@hookform/resolvers/zod";
import { formValues, schema } from "./validations";
import { useForm } from "react-hook-form";
import {
  useAddStrategicInvestmentMutation,
  useGetStrategicInvestment,
  useUpdateStrategicInvestmentMutation,
} from "@/services";
import { toast } from "sonner";
import { useEffect } from "react";

const useStrategicInvestment = () => {
  const form = useForm<formValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      [AS_ON]: "",
      [KPL]: "",
      [MBAPL]: "",
      [SGFL]: "",
      [SIPL]: "",
    },
  });

  const requiredFields = getRequiredFields(schema);

  const { data: investmentData, isLoading: isLoadingInvestment } =
    useGetStrategicInvestment({});

  useEffect(() => {
    if (investmentData) {
      form.reset({
        [AS_ON]: investmentData?.data?.[AS_ON]?.toString() || "",
        [KPL]: investmentData?.data?.[KPL]?.toString() || "",
        [MBAPL]: investmentData?.data?.[MBAPL]?.toString() || "",
        [SGFL]: investmentData?.data?.[SGFL]?.toString() || "",
        [SIPL]: investmentData?.data?.[SIPL]?.toString() || "",
      });
    }
  }, [investmentData]);

  const { mutate: addInfo, isPending: isAddPending } =
    useAddStrategicInvestmentMutation({
      onError: (error?: any) => {
        toast.error(
          typeof error?.response?.data?.issues?.error === "string"
            ? error?.response?.data?.issues?.error
            : typeof error?.response?.data?.message === "string"
            ? error?.response?.data?.message
            : "Something went wrong",
          {
            className: "!bg-red-500 !text-white",
          }
        );
        return error?.response?.data?.message;
      },
      onSuccess: () => {
        toast.success("Strategic Investment Added Successfully", {
          className: "!bg-green-600 !text-white",
        });
      },
    });

  const { mutate: updateInfo, isPending: isUpdatePending } =
    useUpdateStrategicInvestmentMutation({
      onError: (error?: any) => {
        toast.error(
          typeof error?.response?.data?.issues?.error === "string"
            ? error?.response?.data?.issues?.error
            : typeof error?.response?.data?.message === "string"
            ? error?.response?.data?.message
            : "Something went wrong",
          {
            className: "!bg-red-500 !text-white",
          }
        );
        return error?.response?.data?.message;
      },
      onSuccess: () => {
        toast.success("Strategic Investment Updated Successfully", {
          className: "!bg-green-600 !text-white",
        });
      },
    });

  const onSubmit = (data: any) => {
    let _data = removeEmptyFields(data);
    _data = setEmptyFieldsToNull(_data);

    if (!_data[AS_ON]) {
      form.setError(AS_ON, {
        message: "This Field is required",
      });
      return;
    }

    if (investmentData?.data?._id) {
      updateInfo({ id: investmentData?.data?._id, data: { ..._data } });
    } else {
      addInfo({ data: { ..._data } });
    }
  };

  return {
    form,
    requiredFields,
    onSubmit,
    isLoadingInvestment,
    isAddPending,
    isUpdatePending,
  };
};

export default useStrategicInvestment;
